// src/components/firstAider/kit.tsx — shared bits for the First Aider Callout UI.
//
// Glyphs + the pulsing dot. The pulse cadence is 1.2s — deliberately FASTER
// than Quick Message's 1.6s so the red banner feels more urgent even before
// colour is processed (gotcha #3). Keyframes can't be inline, so each consumer
// renders <FaKeyframes/> once (identical @keyframes blocks are harmless).

import type { CSSProperties } from "react";
import { FA } from "../../lib/firstAider";

export const FA_KEYFRAMES = `
@keyframes fa-pulse-red {
  0%   { box-shadow: 0 0 0 0 rgba(184,54,42,0.70); }
  70%  { box-shadow: 0 0 0 12px rgba(184,54,42,0); }
  100% { box-shadow: 0 0 0 0 rgba(184,54,42,0); }
}
@keyframes fa-pulse-white {
  0%   { box-shadow: 0 0 0 0 rgba(255,238,234,0.85); }
  70%  { box-shadow: 0 0 0 10px rgba(255,238,234,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,238,234,0); }
}
`;

export function FaKeyframes() {
  return <style>{FA_KEYFRAMES}</style>;
}

export function PulseDot({ tone = "red" }: { tone?: "red" | "white" }) {
  const base: CSSProperties = {
    width: 10,
    height: 10,
    borderRadius: "50%",
    display: "inline-block",
    flexShrink: 0,
  };
  const style: CSSProperties =
    tone === "white"
      ? { ...base, background: FA.whiteGlow, animation: "fa-pulse-white 1.2s ease-out infinite" }
      : { ...base, background: FA.red, animation: "fa-pulse-red 1.2s ease-out infinite" };
  return <span aria-hidden="true" style={style} />;
}

// --- glyphs -----------------------------------------------------------------

/** Eight-spoke first-aid / emergency asterisk. */
export function EmergencyGlyph({ size = 24, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M12 2v4" />
      <path d="M12 18v4" />
      <path d="M4.93 4.93l2.83 2.83" />
      <path d="M16.24 16.24l2.83 2.83" />
      <path d="M2 12h4" />
      <path d="M18 12h4" />
      <path d="M4.93 19.07l2.83-2.83" />
      <path d="M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

export function SendGlyph({ size = 18, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M22 2 11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

export function CheckGlyph({ size = 14, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function CrossGlyph({ size = 14, color = "currentColor" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M18 6 6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

function BackArrow({ size = 20, color = "#FFFFFF" }: { size?: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>
  );
}

// --- the red emergency top bar (compose + incident) ------------------------

export function FaTopBar({
  title,
  sub,
  onBack,
  pulsing = true,
}: {
  title: string;
  sub: string;
  onBack: () => void;
  pulsing?: boolean;
}) {
  return (
    <>
      <div style={topBarStyle}>
        <button type="button" onClick={onBack} aria-label="Back" style={backBtnStyle}>
          <BackArrow />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={topBarSubStyle}>{sub}</div>
          <div style={topBarTitleStyle}>{title}</div>
        </div>
        {pulsing && <PulseDot tone="white" />}
      </div>
      <FaKeyframes />
    </>
  );
}

const topBarStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  background: `linear-gradient(180deg, ${FA.red} 0%, ${FA.redDk} 100%)`,
  color: "#FFFFFF",
  position: "sticky",
  top: 0,
  zIndex: 20,
  boxShadow: "0 2px 10px rgba(184,54,42,0.30)",
};

const backBtnStyle: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 10,
  background: "rgba(255,255,255,0.16)",
  border: "1px solid rgba(255,255,255,0.28)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  padding: 0,
};

const topBarSubStyle: CSSProperties = {
  fontFamily: "'Nunito','Segoe UI',sans-serif",
  fontSize: 9.5,
  fontWeight: 800,
  letterSpacing: 1.4,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.9)",
};

const topBarTitleStyle: CSSProperties = {
  fontFamily: "'Playfair Display','Times New Roman',serif",
  fontSize: 19,
  fontWeight: 700,
  letterSpacing: -0.2,
  color: "#FFFFFF",
  lineHeight: 1.1,
};
